import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Fragment, useEffect, useState } from 'react';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp,
  GridCellParams
} from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { isObjectEmpty } from '../../../utils/helpers';
import {
  createTFUSetting,
  TFUSetting,
  updateTFUSetting,
  getTFUSettingsSubject,
  getTFUSettingLoadingSubject,
  fetchTFUSettings
} from '../../../store/TFUSetting';
import { TicketFollowUpEscalation } from '../../../store/TFU';
import { PageCard } from '../CardWithTitle';
import { Icon } from '../../Atoms/Icon';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { TextSpan } from '../../Atoms/Text';
import { fetchUsersByRole, UserRole, getUsersByRolesSubject, User } from '../../../store/User';

export const TFUSettingsTable = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [owners, setOwners] = useState<User[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'dueOffset' } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });

    const editedRow = rows.find((row) => (row as TFUSetting).id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row) => (row as TFUSetting).id !== id));
    }
  };

  const handleAddClick = () => {
    const newId = v4();

    setRows((oldRows) => [
      {
        id: newId,
        dueOffset: null,
        escalation: missingEscalations[0],
        ownerId: null,
        owner: null,
        isNew: true
      },
      ...oldRows
    ]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'dueOffset' }
    }));
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = {
      ...newRow,
      isNew: newRow.isNew || false,
      ownerId: newRow.ownerId || null
    } as unknown as TFUSetting;
    const owner = owners.find((o) => o.id === updatedRow.ownerId);
    updatedRow.owner = owner || null;
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));

    // Preform Api Update
    const tfuUpdate = JSON.parse(JSON.stringify(updatedRow));
    if (tfuUpdate.isNew) delete tfuUpdate.id;
    delete tfuUpdate.isNew;
    const functionUpdate = tfuUpdate.id ? updateTFUSetting : createTFUSetting;
    functionUpdate(tfuUpdate);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params: GridRowEditStopParams, event: unknown) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event as any).defaultMuiPrevented = true;
    }
  };

  useEffect(() => {
    fetchTFUSettings(true);
    fetchUsersByRole([UserRole.ADMIN, UserRole.ANALYST]);

    const dataSub = getTFUSettingsSubject().subscribe((r) => setRows(r));
    const assetLoadingSub = getTFUSettingLoadingSubject().subscribe((l) => setLoading(l));
    const dataOwners = getUsersByRolesSubject().subscribe((l) => setOwners(l));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (assetLoadingSub) assetLoadingSub.unsubscribe();
      if (dataOwners) dataOwners.unsubscribe();
    };
  }, []);

  const handleIsCellEditable = (params: GridCellParams) => {
    if (params.field === 'ownerId') {
      return params.row.escalation !== TicketFollowUpEscalation.E0;
    }
    return true;
  };

  const existingEscalations = new Set(rows.map((row) => row.escalation));

  const missingEscalations = Object.values(TicketFollowUpEscalation).filter(
    (escalation) => !existingEscalations.has(escalation)
  );

  const ownersList = owners.map((owner) => ({
    value: owner.id,
    label: `${owner.firstName} ${owner.lastName}`
  }));

  ownersList.unshift({
    value: '',
    label: '--No Owner--'
  });

  const tableCols: GridColDef[] = [
    {
      headerName: 'Escalation Level',
      field: 'escalation',
      flex: 1,
      type: 'singleSelect',
      editable: false,
      sortable: false,
      filterable: false,
      valueOptions: Object.values(TicketFollowUpEscalation)
    },
    {
      headerName: 'Owner',
      field: 'ownerId',
      flex: 1,
      type: 'singleSelect',
      editable: true,
      sortable: false,
      filterable: false,
      valueOptions: ownersList,
      renderCell(params) {
        if (params.row.escalation === TicketFollowUpEscalation.E0) {
          return <TextSpan size="sm14">Ticket Assignee</TextSpan>;
        }
        const owner = params.row.owner;
        return owner ? (
          <TextSpan size="sm14">
            {owner.firstName} {owner.lastName}
          </TextSpan>
        ) : (
          <TextSpan
            size="sm14"
            color="grey5">
            No Owner
          </TextSpan>
        );
      }
    },
    {
      headerName: 'Default Due Today +',
      field: 'dueOffset',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell(params) {
        const dueOffset = params.row.dueOffset;
        return dueOffset !== null ? (
          <TextSpan size="sm14">{dueOffset} days</TextSpan>
        ) : (
          <TextSpan
            size="sm14"
            color="grey5">
            No Due Offset
          </TextSpan>
        );
      },
      preProcessEditCellProps: (params) => {
        const value = params.props.value;
        const isValid = Number.isInteger(Number(value));

        return {
          ...params.props,
          error: !isValid
        };
      },
      editable: true,
      sortable: false,
      filterable: false
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon sx={{ color: 'primary.main' }} />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon color="buttonGrey" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
      }
    }
  ];

  const cardActions = () => {
    return !missingEscalations.length ? (
      <></>
    ) : (
      <button
        className="flex items-center space-x-2"
        disabled={!isObjectEmpty(rowModesModel)}
        onClick={handleAddClick}
        data-cy="new-tfu-setting-button"
        data-testid="new-tfu-setting-button">
        <Icon type="plus" />
        <TextSpan
          size="sm14"
          color={!isObjectEmpty(rowModesModel) ? 'grey5' : 'indigo'}>
          New Follow-Up Setting
        </TextSpan>
      </button>
    );
  };

  return (
    <div
      className="w-full"
      data-testid="tfu-settings-table-container">
      <PageCard
        title="Ticket Follow-Ups"
        cardActions={cardActions()}>
        <Fragment>
          {loading && <ComponentSpinner label="Loading TFUSetting Data" />}
          {!loading && (
            <DataGridPro
              rows={rows}
              sx={{
                ...DEFAULT_MINI_TABLE_SX_STYLES,
                maxHeight: '30rem',
                overflow: 'auto',
                border: 'unset'
              }}
              rowHeight={40}
              columnHeaderHeight={40}
              showCellVerticalBorder
              hideFooter
              showColumnVerticalBorder
              initialState={{
                sorting: {
                  sortModel: [{ field: 'escalation', sort: 'asc' }]
                }
              }}
              columns={tableCols}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              processRowUpdate={processRowUpdate}
              onRowEditStop={handleRowEditStop}
              isCellEditable={handleIsCellEditable}
            />
          )}
        </Fragment>
      </PageCard>
    </div>
  );
};
